import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { postsAPI, categoriesAPI, mediaAPI } from '../../lib/api';
import { slugify } from '../../lib/utils';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { FeaturedImageCard } from '../../components/editor/FeaturedImageCard';
import { CategorySelector } from '../../components/editor/CategorySelector';
import { AuthorCard, TagsCard, SettingsCard, SEOCard } from '../../components/editor/SidebarCards';
import { KeyTakeawaysCard, FAQCard } from '../../components/editor/ContentCards';

const INITIAL_FORM = {
  title: '', slug: '', excerpt: '', content: '',
  featured_image: '', featured_image_alt: '', featured_image_position: 'center',
  category_id: '', category_ids: [],
  author_name: 'Equipe Apex Medical Hub',
  author_bio: 'Especialistas em gestão financeira, tributária e operacional para clínicas e consultórios médicos.',
  tags: [], key_takeaways: [], faq_items: [],
  reading_time: 5, status: 'draft', is_featured: false,
  seo_title: '', seo_description: '',
};

const quillModules = {
  toolbar: [
    [{ 'header': [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['blockquote', 'link'],
    ['clean']
  ],
};

const PostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    categoriesAPI.getAll().then(r => setCategories(r.data)).catch(() => toast.error('Erro ao carregar categorias'));
    if (isEditing) {
      setLoading(true);
      postsAPI.getForEdit(id)
        .then(r => setFormData({ ...r.data, tags: r.data.tags || [], key_takeaways: r.data.key_takeaways || [], faq_items: r.data.faq_items || [], category_ids: r.data.category_ids || [] }))
        .catch(() => { toast.error('Erro ao carregar artigo'); navigate('/admin/posts'); })
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, navigate]);

  const handleChange = (field, value) => {
    if (field === 'title' && !isEditing) {
      setFormData(prev => ({ ...prev, title: value, slug: slugify(value) }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleToggleCategory = (categoryId) => {
    const currentIds = formData.category_ids || [];
    if (currentIds.includes(categoryId)) {
      const newIds = currentIds.filter(id => id !== categoryId);
      setFormData(prev => ({ ...prev, category_ids: newIds, category_id: newIds.length > 0 ? (prev.category_id === categoryId ? newIds[0] : prev.category_id) : '' }));
    } else {
      const newIds = [...currentIds, categoryId];
      setFormData(prev => ({ ...prev, category_ids: newIds, category_id: prev.category_id || categoryId }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const response = await mediaAPI.upload(file);
      setFormData(prev => ({ ...prev, featured_image: `/api/media/${response.data.id}` }));
      toast.success('Imagem enviada com sucesso');
    } catch { toast.error('Erro ao enviar imagem'); }
  };

  const handleSave = async (status = formData.status) => {
    const hasCategory = formData.category_id || (formData.category_ids && formData.category_ids.length > 0);
    if (!formData.title || !formData.content || !hasCategory) {
      toast.error('Preencha título, conteúdo e selecione ao menos uma categoria');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) { await postsAPI.update(id, { ...formData, status }); toast.success('Artigo atualizado com sucesso'); }
      else { await postsAPI.create({ ...formData, status }); toast.success('Artigo criado com sucesso'); }
      navigate('/admin/posts');
    } catch { toast.error('Erro ao salvar artigo'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div data-testid="post-editor">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/posts')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar
          </Button>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{isEditing ? 'Editar Artigo' : 'Novo Artigo'}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => handleSave('draft')} disabled={saving}>Salvar Rascunho</Button>
          <Button className="btn-primary" onClick={() => handleSave('published')} disabled={saving} data-testid="publish-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Publicar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input id="title" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Título do artigo" className="mt-1 text-lg" data-testid="post-title" />
              </div>
              <div>
                <Label htmlFor="slug">Slug (URL)</Label>
                <Input id="slug" value={formData.slug} onChange={(e) => handleChange('slug', e.target.value)} placeholder="url-do-artigo" className="mt-1" data-testid="post-slug" />
              </div>
              <div>
                <Label htmlFor="excerpt">Resumo *</Label>
                <Textarea id="excerpt" value={formData.excerpt} onChange={(e) => handleChange('excerpt', e.target.value)} placeholder="Breve descrição do artigo" rows={3} className="mt-1" data-testid="post-excerpt" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Conteúdo *</CardTitle></CardHeader>
            <CardContent>
              <ReactQuill theme="snow" value={formData.content} onChange={(value) => handleChange('content', value)} modules={quillModules} placeholder="Escreva o conteúdo do artigo..." className="bg-white" data-testid="post-content" />
            </CardContent>
          </Card>
          <KeyTakeawaysCard formData={formData} onChange={handleChange} />
          <FAQCard formData={formData} onChange={handleChange} />
        </div>
        <div className="space-y-6">
          <FeaturedImageCard formData={formData} onChange={handleChange} onUpload={handleImageUpload} />
          <CategorySelector categories={categories} formData={formData} onToggle={handleToggleCategory} />
          <AuthorCard formData={formData} onChange={handleChange} />
          <TagsCard formData={formData} onChange={handleChange} />
          <SettingsCard formData={formData} onChange={handleChange} />
          <SEOCard formData={formData} onChange={handleChange} />
        </div>
      </div>
    </div>
  );
};

export default PostEditor;
